import { type AuthError, signInWithEmailLink } from 'firebase/auth';
import { type UserCredential } from '@linktivity/link-utils';
import { AdminMixerService } from '@linktivity/triplabo-taxfree';
import {
  LOGIN_PATH,
  Role,
  ROLE_SCOPE,
  STAFF_MANAGER_ROLES,
  type Scope
} from '@link/admin/constants';
import { config as env } from '@link/admin/utils/env';
import adminAuth from '@link/admin/vendors/firebase';
import { CommonAuthType } from './enum';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const AUTH_TYPE_KEY = 'auth_type';
const EMAIL_KEY = 'email';
const EMAIL_FOR_SIGN_IN_KEY = 'email_for_sign_in';

export const AUTH_HEADER = 'grpc-metadata-authorization';

export const getAccessToken = () =>
  localStorage.getItem(ACCESS_TOKEN_KEY) ?? '';

export const getRefreshToken = () =>
  localStorage.getItem(REFRESH_TOKEN_KEY) ?? '';

export const getAuthType = () =>
  (localStorage.getItem(AUTH_TYPE_KEY) ?? '') as CommonAuthType;

export const getEmail = () => localStorage.getItem(EMAIL_KEY) ?? '';

export const setTokens = (
  accessToken: string,
  refreshToken: string,
  authType: CommonAuthType
) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(AUTH_TYPE_KEY, authType);
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH_TYPE_KEY);
  localStorage.removeItem(EMAIL_KEY);
};

export const isAuthenticated = () => Boolean(getAccessToken());

const ROLE_BY_AUTH_TYPE: Partial<Record<CommonAuthType, Role>> = {
  [CommonAuthType.AuthTypeLinkAdmin]: Role.Link,
  [CommonAuthType.AuthTypeLinkCs]: Role.Link,
  [CommonAuthType.AuthTypeMerchantRootAdmin]: Role.MerchantAdmin,
  [CommonAuthType.AuthTypeMerchantRootStaff]: Role.MerchantStaff,
  [CommonAuthType.AuthTypeMerchantGroupAdmin]: Role.ShopAdmin,
  [CommonAuthType.AuthTypeMerchantGroupStaff]: Role.ShopStaff
};

export const getRole = (): Role | undefined => ROLE_BY_AUTH_TYPE[getAuthType()];

export const getScope = (): Scope | undefined => {
  const role = getRole();

  return role && ROLE_SCOPE[role];
};

export const canManageStaff = () => {
  const role = getRole();

  return Boolean(role && STAFF_MANAGER_ROLES.includes(role));
};

export const logout = async () => {
  await adminAuth.logout();
  clearTokens();
};

export const redirectToLogin = () => {
  void adminAuth.logout();
  clearTokens();

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.replace(LOGIN_PATH);
  }
};

export const accountApi = new AdminMixerService.AccountApi(
  new AdminMixerService.Configuration({ basePath: env.apiBase })
);

let refreshing: Promise<boolean> | null = null;

const requestNewTokens = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const tokens = await accountApi.adminMixerServiceRefresh({
      body: { refreshToken }
    });

    setTokens(tokens.accessToken, tokens.refreshToken, tokens.authType);
    return true;
  } catch (error) {
    console.error(error);
    clearTokens();
    return false;
  }
};

export const refreshAccessToken = async () => {
  if (refreshing) {
    return refreshing;
  }

  refreshing = requestNewTokens();

  try {
    return await refreshing;
  } finally {
    refreshing = null;
  }
};

const ACCOUNT_ERROR_STATUS = [401, 403, 404];

export const isAccountError = (error: unknown) =>
  error instanceof AdminMixerService.ResponseError &&
  ACCOUNT_ERROR_STATUS.includes(error.response.status);

const exchangeToken = async (credential: UserCredential) => {
  const idToken = await credential.user.getIdToken();

  try {
    const tokens = await accountApi.adminMixerServiceLogin({
      body: { idToken }
    });

    setTokens(tokens.accessToken, tokens.refreshToken, tokens.authType);
    localStorage.setItem(EMAIL_KEY, credential.user.email ?? '');

    return tokens;
  } catch (error) {
    await adminAuth.logout();
    throw error;
  }
};

export const isFirebaseAuthError = (error: unknown) =>
  error instanceof Error &&
  'code' in error &&
  String(error.code).startsWith('auth/');

export const login = async () => {
  const credential = await adminAuth.googleLogin();

  if (!credential) {
    return;
  }

  return exchangeToken(credential);
};

const withFirebaseError = async <T>(task: () => Promise<T>) => {
  try {
    return await task();
  } catch (error) {
    if (isFirebaseAuthError(error)) {
      adminAuth.showErrorMessage(error as AuthError);
    }
    throw error;
  }
};

export const sendLoginLink = async (email: string) => {
  await accountApi.adminMixerServiceSendLoginEmail({ body: { email } });
  localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
};

const getLinkParam = (key: string) =>
  new URLSearchParams(window.location.search).get(key) ?? '';

const toFirebaseLink = (oobCode: string) => {
  const params = new URLSearchParams({
    apiKey: env.firebaseApiKey,
    oobCode,
    mode: 'signIn'
  });

  if (env.firebaseTenantId) {
    params.set('tenantId', env.firebaseTenantId);
  }

  return `${window.location.origin}${LOGIN_PATH}?${params}`;
};

export const isLoginLink = () => Boolean(getLinkParam('oobCode'));

export const getEmailForSignIn = () =>
  getLinkParam('email') || (localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY) ?? '');

export const loginWithLink = async (email: string) => {
  const link = toFirebaseLink(getLinkParam('oobCode'));
  const credential = await withFirebaseError(() =>
    signInWithEmailLink(adminAuth.auth, email, link)
  );

  localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);

  return exchangeToken(credential);
};
